/**
 * Helpers for inferring sport-specific position groups and role types
 * from free-form coach messages (e.g. "He plays infield and outfield").
 *
 * Pure functions, no React/Supabase dependencies.
 */

import { getSportPositionGroups } from '../../../utils/sportUtils';
import { getRoleTypesForSport } from '../../../types/playerCard';

/** Naive English singularizer — good enough for the position-group names we match against. */
function toSingular(word: string): string {
  if (word.endsWith('ies') && word.length > 3) return `${word.slice(0, -3)}y`;
  if (/(?:s|x|z|ch|sh)es$/.test(word)) return word.slice(0, -2);
  if (word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

/** A group name's plural form plus singular variants, so "center"/"centers" both match "Centers". */
function groupVariants(group: string): string[] {
  const lower = group.toLowerCase();
  const words = lower.split(/\s+/);
  const variants = new Set<string>([lower, words.map(toSingular).join(' ')]);
  if (words.length > 1) {
    variants.add([...words.slice(0, -1), toSingular(words[words.length - 1])].join(' '));
  }
  return Array.from(variants);
}

/** Whether `text` (already lowercased) mentions a position group, singular or plural. */
function textMentionsGroup(lower: string, group: string): boolean {
  return groupVariants(group).some((variant) => {
    const escaped = variant.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`\\b${escaped}\\b`, 'i').test(lower);
  });
}

/**
 * Pick the position groups mentioned in `text`, intersected with the
 * groups valid for the team's sport.
 */
export function extractPositionGroupsFromText(text: string, sport: string): string[] {
  const lower = text.toLowerCase();
  const allowed = getSportPositionGroups(sport) || [];
  const picked = new Set<string>();

  if (sport === 'Baseball' || sport === 'Softball') {
    if (/\binfield\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('infield'));
      if (g) picked.add(g);
    }
    if (/\boutfield\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('outfield'));
      if (g) picked.add(g);
    }
    if (/\bpitch(er|ing|ers)\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('pitch'));
      if (g) picked.add(g);
    }
    if (/\bcatch(er|ing|ers)\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('catch'));
      if (g) picked.add(g);
    }
    if (/\b1st\b|\bfirst\s+base\b|\b1b\b/.test(lower)) {
      const g = allowed.find((x) => x.toLowerCase().includes('1st'));
      if (g) picked.add(g);
    }
  }

  for (const group of allowed) {
    if (textMentionsGroup(lower, group)) picked.add(group);
  }

  if (sport === 'Football' || sport === 'FlagFootball') {
    for (const group of allowed) {
      const re = new RegExp(`\\b${group.toLowerCase()}\\b`, 'i');
      if (re.test(lower)) picked.add(group);
    }
  }

  return Array.from(picked);
}

/**
 * Map a list of position groups to coarse role types (e.g. "battery"
 * for Baseball pitchers/catchers) used by the player card.
 */
export function inferRoleTypesFromGroups(sport: string, groups: string[]): string[] {
  const allowed = getRoleTypesForSport(sport) || [];
  if (allowed.length === 0) return [];

  const lowerGroups = groups.map((g) => g.toLowerCase());

  if (sport === 'Baseball' || sport === 'Softball') {
    const roles = new Set<string>();
    if (lowerGroups.some((g) => g.includes('pitch') || g.includes('catch'))) roles.add('battery');
    if (lowerGroups.some((g) => g.includes('infield') || g.includes('1st'))) roles.add('infield');
    if (lowerGroups.some((g) => g.includes('outfield'))) roles.add('outfield');
    const out = Array.from(roles).filter((r) => allowed.includes(r));
    return out.length ? out : allowed;
  }

  if (sport === 'Football' || sport === 'FlagFootball') {
    const roles = new Set<string>();
    if (groups.some((g) => ['OL', 'DL', 'EDGE'].includes(g))) roles.add('line');
    if (groups.some((g) => ['QB', 'WR', 'RB', 'TE', 'LB', 'DB', 'DL', 'EDGE', 'OL'].includes(g))) roles.add('skill');
    const out = Array.from(roles).filter((r) => allowed.includes(r));
    return out.length ? out : allowed;
  }

  const roles = new Set<string>();
  for (const g of lowerGroups) {
    for (const role of allowed) {
      const r = role.toLowerCase();
      if (g.includes(r) || g.includes(`${r}s`)) roles.add(role);
    }
  }
  const out = Array.from(roles);
  return out.length ? out : allowed;
}
